import naclutil from 'tweetnacl-util';
import Helpers from '../base/Helpers';
import RequestBehaviour from '../base/RequestBehaviour';
import PaymentTransaction from '../requests/PaymentTransaction';
import FailedEncryptionException from '../exceptions/FailedEncryptionException';
import AuthTokenization from './AuthTokenization';
import SaleTransaction from './SaleTransaction';

export default class LookupTransaction extends RequestBehaviour {
	/**
	 * Tokenized card identifier (T-* format)
	 */
	card_token?: string | null = undefined;

	/**
	 * Card number or PAN
	 */
	card_number?: string | null = undefined;

	/**
	 * Card security code
	 */
	card_cvv?: string | null = undefined;

	/**
	 * Card expire year/month date (YYMM)
	 */
	card_expire?: string | null = undefined;

	/**
	 * Customer billing address
	 */
	billing_address?: string | null = undefined;

	/**
	 * Customer billing country alpha-2 code (ISO 3166-1)
	 */
	billing_country?: string = undefined;

	/**
	 * Customer billing state alpha code (ISO 3166-2)
	 */
	billing_state?: string = undefined;

	/**
	 * Customer billing city
	 */
	billing_city?: string | null = undefined;

	/**
	 * Customer billing postal code
	 */
	billing_zip?: string = undefined;

	/**
	 * Customer billing phone
	 */
	billing_phone?: string = undefined;

	/**
	 * Order customer name
	 */
	customer_name?: string | null = undefined;

	/**
	 * Order customer email
	 */
	customer_email?: string | null = undefined;

	/**
	 * Order ID
	 */
	order_id?: string = undefined;

	/**
	 * Order currency code alpha-3
	 */
	order_currency?: string = undefined;

	/**
	 * Order total amount
	 */
	order_amount?: string | null = undefined;

	/**
	 * Indicates the maximum number of authorizations for installment payments.
	 */
	installment?: string = undefined;

	/**
	 * Secure payload for transactions
	 */
	secure_payload?: string = undefined;

	/**
	 * Wallet payload for Apple Pay transactions
	 */
	apple_pay?: string = undefined;

	/**
	 * Wallet payload for Google Pay transactions
	 */
	google_pay?: string = undefined;

	/**
	 * Wallet payload for Click to Pay transactions
	 */
	click_to_pay?: string = undefined;

	/**
	 * Cardinal session reference
	 */
	reference?: string = undefined;

	/**
	 * Ability of the cardholder browser to execute Java.
	 */
	browser_java_enabled: string = 'false';

	/**
	 *  Exact content of the HTTP accept headers sent from the cardholder's browser.
	 */
	browser_header?: string = undefined;

	/**
	 * Browser language as defined in IETF BCP47 .
	 */
	browser_language?: string = undefined;

	/**
	 * The bit depth of the color palette for displaying images, in bits per pixel
	 */
	browser_color_depth?: string = undefined;

	/**
	 * Total height of the Cardholder's scree in pixels
	 */
	browser_screen_height?: string = undefined;

	/**
	 * Total width of the Cardholder's scree in pixels
	 */
	browser_screen_width?: string = undefined;

	/**
	 * Time difference between UTC time and the cardholder browser local time, in minutes
	 */
	browser_time_zone?: string = undefined;

	/**
	 *  The exact content of the HTTP user agent header.
	 */
	user_agent?: string = undefined;

	/**
	 * The IP address of the Consumer.
	 */
	ip_address?: string = undefined;

	/**
	 * Cardinal device channel (SDK or Browser)
	 */
	device_channel: string = 'Browser';

	/**
	 * Ability of the cardholder browser to execute JavaScript.
	 */
	browser_javascript_enabled: string = 'true';

	/**
	 * Cardinal authentication flag for tokenization
	 */
	authenticate_token: boolean = false;

	/**
	 * Cast PaymentTransaction to LookupTransaction
	 */
	fromPaymentTransaction(request: PaymentTransaction) {
		this.card_token = request.card_token;
		this.card_number = request.card_number;
		this.card_expire = request.card_expire;

		this.billing_address = request.billing_address;
		this.billing_country = request.billing_country;
		this.billing_state = request.billing_state;
		this.billing_city = request.billing_city;
		this.billing_zip = request.billing_zip;
		this.billing_phone = request.billing_phone;

		this.customer_name = request.customer_name;
		this.customer_email = request.customer_email;

		this.order_id = request.order_id;
		this.order_currency = request.order_currency;
		this.order_amount = request.order_amount;

		this.secure_payload = request.secure_payload;
		this.apple_pay = request.apple_pay;
		this.google_pay = request.google_pay;
		this.click_to_pay = request.click_to_pay;

		this.authenticate_token = request instanceof AuthTokenization;

		if (request instanceof SaleTransaction) {
			this.installment = request.installment_months;
		}

		this.sdk_key_pair = request.getSDKKeyPair();
	}

	/**
	 * Check if required EMV fields were included by songbird. If not, they are added manually.
	 */
	setRequiredEMVFields() {
		if (!this.browser_java_enabled) {
			this.browser_java_enabled = typeof navigator.javaEnabled === "function"
				? navigator.javaEnabled().toString()
				: 'false';
		}

		if (!this.browser_language) {
			this.browser_language = navigator.language;
		}

		if (!this.browser_color_depth) {
			this.browser_color_depth = screen.colorDepth.toString();
		}

		if (!this.browser_screen_height) {
			this.browser_screen_height = screen.height.toString() || window.innerHeight.toString();
		}

		if (!this.browser_screen_width) {
			this.browser_screen_width = screen.width.toString() || window.innerWidth.toString();
		}

		if (!this.browser_time_zone) {
			this.browser_time_zone = (new Date()).getTimezoneOffset().toString();
		}

		if (!this.user_agent) {
			this.user_agent = navigator.userAgent;
		}
	}

	/**
	 * Check if current request needs encryption.
	 *
	 * @return
	 */
	isEncryptable(): boolean {
		return Boolean(this.card_number || this.card_expire || this.card_cvv || this.customer_email);
	}

	/**
	 * Encrypt fields with sensitive data.
	 *
	 * @param publicKey
	 * @return
	 * @throws FailedEncryptionException
	 */
	withEncryption(publicKey: string | null): string {
		if (!publicKey) {
			throw new FailedEncryptionException('Could not process transaction without merchant public key.');
		}

		try {
			const keyPair = this.sdk_key_pair ?? Helpers.getKeyPair();

			if (this.card_number) this.card_number = Helpers.encrypt(this.card_number, publicKey, keyPair);
			if (this.card_expire) this.card_expire = Helpers.encrypt(this.card_expire, publicKey, keyPair);
			if (this.card_cvv) this.card_cvv = Helpers.encrypt(this.card_cvv, publicKey, keyPair);
			if (this.customer_email) this.customer_email = Helpers.encrypt(this.customer_email, publicKey, keyPair);

			const sdk_public_key = naclutil.encodeBase64(keyPair.publicKey);

			this.sdk_key_pair = keyPair;

			return sdk_public_key;
		} catch (error) {
			throw new FailedEncryptionException('Encryption process encountered an unexpected error.');
		}
	}
}
